import { useState, useMemo } from "react";
import { Flex, Loader, SegmentedControl, Select, Stack, Text } from "@mantine/core";
import { useSignalDocuments, useWorkspaceSkills } from "../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import useObjectDrawerStore from "../../../store/drawer/objectDrawerStore";
import type { ObjectDrawerType } from "../../../store/drawer/objectDrawerStore";
import SignalDocumentCard from "../signals/signal-documents/SignalDocumentCard";
import type { SignalDocument } from "../../../services/signalDocuments/signalDocumentsEndpoints.interface";

const InboxPage = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const openDrawer = useObjectDrawerStore((state) => state.openDrawer);

  const [skillFilter, setSkillFilter] = useState<string | null>(null);
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>("all");

  const { data: documents, isLoading: docsLoading } = useSignalDocuments({
    workspaceId: workspace?.id,
  }) as { data: SignalDocument[] | undefined; isLoading: boolean };

  const { data: skills } = useWorkspaceSkills({
    workspaceId: workspace?.id,
  }) as { data: any[] | undefined };

  const skillOptions = useMemo(
    () =>
      (skills || []).map((skill: any) => ({
        label: skill.display_name || skill.skill_name,
        value: skill.id,
      })),
    [skills],
  );

  const filteredDocuments = useMemo(() => {
    if (!documents) return [];
    return documents.filter((doc) => {
      if (doc.signal_type === "health") return false;
      if (skillFilter) {
        const matchesSkill = doc.skills_used?.some(
          (s) => s.skill_id === skillFilter,
        );
        if (!matchesSkill) return false;
      }
      if (entityTypeFilter !== "all" && doc.entity_type !== entityTypeFilter) {
        return false;
      }
      return true;
    });
  }, [documents, skillFilter, entityTypeFilter]);

  const handleDocClick = (doc: SignalDocument) => {
    if (!doc.entity_type || !doc.entity_external_id) return;
    openDrawer({
      objectType: doc.entity_type as ObjectDrawerType,
      objectId: doc.entity_external_id,
    });
  };

  return (
    <div className="h-full overflow-auto bg-background">
      <div className="mx-auto max-w-5xl px-6 py-6">
        <Stack gap="md">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Inbox</h1>
            <p className="text-sm text-muted-foreground">
              AI analysis documents from your skill executions
            </p>
          </div>

          <Flex align="center" gap="sm" wrap="wrap">
            <Select
              value={skillFilter}
              onChange={setSkillFilter}
              placeholder="All skills"
              data={skillOptions}
              clearable
              size="sm"
              classNames={{
                input: "rounded-md text-sm",
              }}
            />
            <SegmentedControl
              color="main.5"
              size="sm"
              value={entityTypeFilter}
              onChange={setEntityTypeFilter}
              className="bg-white border border-gray-200"
              data={[
                { value: "all", label: "All" },
                { value: "accounts", label: "Accounts" },
                { value: "users", label: "Users" },
              ]}
            />
          </Flex>

          {docsLoading ? (
            <Flex justify="center" py="xl">
              <Loader color="main.5" />
            </Flex>
          ) : filteredDocuments.length === 0 ? (
            <Flex direction="column" align="center" py="xl" gap="xs">
              <Text size="lg" fw={500} c="dimmed">
                No documents found
              </Text>
              <Text size="sm" c="dimmed">
                {documents?.length
                  ? "Try adjusting your filters"
                  : "AI analysis documents will appear here after skill executions"}
              </Text>
            </Flex>
          ) : (
            <Stack gap="md">
              {filteredDocuments.map((doc) => (
                <div
                  key={doc.id}
                  onClick={() => handleDocClick(doc)}
                  className="cursor-pointer transition-shadow hover:shadow-md rounded-lg"
                >
                  <SignalDocumentCard
                    document={doc}
                    workspaceId={workspace?.id || ""}
                  />
                </div>
              ))}
            </Stack>
          )}
        </Stack>
      </div>
    </div>
  );
};

export default InboxPage;
